import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import Constants from 'expo-constants';

import config from '../config';
import { store } from '../store';
import debug from '../utilities/debug';

interface Props {
  children: React.ReactNode;
  /** Optional label shown in the Slack notification to identify where in the tree the crash occurred */
  boundary?: string;
  /** Custom fallback. If provided it replaces the default error screen. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

async function sendToSlack(error: Error, info: React.ErrorInfo, boundary: string): Promise<void> {
  const hookUrl = config.slack.errorHookUrl;
  if (!hookUrl) return;

  try {
    const state = store.getState();
    const user = state?.authentication?.user;
    const account = state?.session?.account;

    const userName = user
      ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || 'Unknown'
      : 'Unauthenticated';
    const email = user?.email ?? 'N/A';
    const cognitoId = user?.cognito_id ?? 'N/A';
    const accountId = account?.account_id ?? 'N/A';

    const appVersion = Constants.expoConfig?.version ?? 'unknown';
    const buildNumber =
      Platform.OS === 'ios'
        ? Constants.expoConfig?.ios?.buildNumber ?? 'unknown'
        : Constants.expoConfig?.android?.versionCode ?? 'unknown';
    const platform = `${Platform.OS} ${Platform.Version}`;
    const env = config.env;
    const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });

    // Keep stack trace under Slack's 3000-char mrkdwn limit per block
    const stack = (info.componentStack ?? error.stack ?? '').slice(0, 2000);

    const message =
      `*📱 Mobile App Crash — ${boundary}*\n` +
      `*Error:* ${error.message}\n` +
      `*Environment:* ${env} | ${platform}\n` +
      `*App Version:* ${appVersion} (${buildNumber})\n` +
      `*User:* ${userName}\n` +
      `*Email:* ${email}\n` +
      `*Cognito ID:* ${cognitoId}\n` +
      `*Account ID:* ${accountId}\n` +
      `*Date:* ${now}\n` +
      `*Component Stack:* \`\`\`${stack}\`\`\``;

    await fetch(hookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        blocks: [
          {
            type: 'section',
            text: { type: 'mrkdwn', text: message },
          },
        ],
      }),
    });
  } catch (slackErr) {
    debug.error('ErrorBoundary: failed to post to Slack', slackErr);
  }
}

export default class ErrorBoundary extends React.Component<Props, State> {
  static defaultProps = { boundary: 'Root' };

  state: State = { error: null, info: null };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ error, info });
    sendToSlack(error, info, this.props.boundary ?? 'Root');
    debug.error('ErrorBoundary caught', { boundary: this.props.boundary, message: error.message });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    const { children, fallback } = this.props;

    if (!error) return <>{children}</>;

    if (fallback) return <>{fallback(error, this.reset)}</>;

    const stack = (info?.componentStack ?? error.stack ?? '').trim();

    return (
      <View style={styles.container}>
        <Text style={styles.title}>Something went wrong</Text>
        <Text style={styles.subtitle}>
          The app ran into an unexpected error. Our team has been notified.
        </Text>

        <TouchableOpacity style={styles.button} onPress={this.reset} activeOpacity={0.8}>
          <Text style={styles.buttonText}>Try Again</Text>
        </TouchableOpacity>

        <ScrollView style={styles.devBox} contentContainerStyle={styles.devBoxContent}>
          <Text style={styles.devTitle}>{error.message}</Text>
          <Text style={styles.devStack}>{stack}</Text>
        </ScrollView>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    padding: 24,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    color: '#555',
    textAlign: 'center',
    marginBottom: 28,
    lineHeight: 22,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 10,
    backgroundColor: '#F36221',
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
  },
  devBox: {
    marginTop: 24,
    maxHeight: 280,
    width: '100%',
    backgroundColor: '#f4f4f4',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  devBoxContent: {
    padding: 12,
  },
  devTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#c0392b',
    marginBottom: 8,
  },
  devStack: {
    fontSize: 11,
    color: '#444',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    lineHeight: 16,
  },
});
